/* eslint-disable react/jsx-props-no-spreading */
import React, { useEffect } from 'react';
import { NextComponentType, NextPageContext } from 'next';
import Head from 'next/head';
import { useRouter } from 'next/router';

import { metaTags, linkTags, getPaginationLinks, removeMetaTags } from 'utils/seoHead';

import ThemeProvider, { ThemeEnum } from 'ui/src/components/Theme';

import BairesDevHeader from 'layout/src/components/header';
import BairesDevFooter from 'layout/src/components/footer';
import ButtonArrowIcon from 'ui/src/icons/ButtonArrowIcon';
import EmailIcon from 'ui/src/icons/EmailIcon';
import type { FooterMenu } from 'layout/src/data/FooterMenuType';

import { getMenuTree } from 'utils/shared';
import seoScriptProps from 'utilities/src/seoScriptProps';

import mutiny from 'tracking/src/mutiny';

import { MenuLocationEnum } from 'gql/graphql-operations';

import ButtonLink from 'ui/src/components/Button/ButtonLink';
import { parseImageUrl } from 'utils/imageUtils';
import type MenuItem from 'ui/src/data/MenuItem';
import CookieBar from 'layout/src/components/cookieBar';
import addUrlHostName from 'ui/src/utils/addUrlHostname';
import extractText from 'utilities/src/extractText';

import Navigation from 'components/Navigation';

const mainMenuMockData: MenuItem[] = [
  {
    id: 'megamenTempID',
    path: '/software-development-services/',
    label: 'Services',
    children: [
      {
        id: 'sdbsfrteqebwrt',
        path: 'https://www.bairesdev.com/software-development-services/',
        label: 'Software Development Services',
        description:
          'Nearshore your software development. <br> <a href="https://www.bairesdev.com/hire-software-developers/">Hire software developers</a> through flexible engagement models:',
        children: [
          {
            id: 'cG9zdDo2MDkwMg==',
            path: '/staff-augmentation/',
            label: 'Staff Augmentation',
            description: 'Our software developers in your team.',
            icon: 'StaffAugmentation',
            children: [],
          },
          {
            id: 'cG9zdDafgo2fMDkwdMQ==',
            path: '/software-development-services/software-dedicated-team/',
            label: 'Dedicated Teams',
            description: 'Our software teams in your organization.',
            icon: 'DedicatedTeams',
            children: [],
          },
          {
            id: 'cG9zdDo2MDkwMw==',
            path: '/software-development-services/software-outsourcing/',
            label: 'Software Outsourcing',
            description: 'Our PM and software teams building for you.',
            icon: 'SoftwareOutsourcing',
            children: [],
          },
        ],
      },
      {
        id: 'wertwtytygfw',
        path: 'https://www.bairesdev.com/technologies/',
        label: 'Technologies',
        children: [
          {
            id: 'cG9zdDo2MdfsDkwMQ==',
            path: 'https://www.bairesdev.com/technologies/react/',
            label: 'React',
            children: [],
          },
          {
            id: 'cG9zdDo2sdfasdfMDkwMg==',
            path: 'https://www.bairesdev.com/technologies/nodejs/',
            label: 'Node.js',
            children: [],
          },
          {
            id: 'cG9zdDodfgsdfg2MDkwMw==',
            path: 'https://www.bairesdev.com/technologies/python/',
            label: 'Python',
            children: [],
          },
          {
            id: 'cG9zdDwerterto2MDkwMw==',
            path: 'https://www.bairesdev.com/technologies/net/',
            label: '.NET',
            children: [],
          },
          {
            id: 'cG9zdDo2ertyertMDkwMw==',
            path: 'https://www.bairesdev.com/technologies/java/',
            label: 'Java',
            children: [],
          },
          {
            id: 'cG9zdwerDo2MDkwMw==',
            path: 'https://www.bairesdev.com/technologies/ruby/',
            label: 'Ruby',
            children: [],
          },
          {
            id: 'cG9zdDo2MDkwrtwertMw==',
            path: 'https://www.bairesdev.com/technologies/php/',
            label: 'PHP',
            children: [],
          },
          {
            id: 'cG9qwerqwerzdDo2MDkwMw==',
            path: 'https://www.bairesdev.com/technologies/go/',
            label: 'Golang',
            children: [],
          },
          {
            id: 'cG9zdDo2MDkwwertwerMw==',
            path: 'https://www.bairesdev.com/technologies/angular/',
            label: 'Angular',
            children: [],
          },
        ],
      },
      {
        id: 'ccccergbvbfgcc',
        path: 'https://www.bairesdev.com/solutions/',
        label: 'Solutions',
        children: [
          {
            id: 'cG9zdDo2MasdfDkwMQ==',
            path: 'https://www.bairesdev.com/solutions/qa/',
            label: 'QA Testing & Automation',
            children: [],
          },
          {
            id: 'cG9zdDo2MDfgdkwMg==',
            path: 'https://www.bairesdev.com/solutions/bi/',
            label: 'Business Intelligence',
            children: [],
          },
          {
            id: 'cG9zdDoadfgr2MDkwMw==',
            path: 'https://www.bairesdev.com/solutions/data-science/',
            label: 'Data Science',
            children: [],
          },
          {
            id: 'cG9zdDo2MrtrethytrhDkwMQ==',
            path: 'https://www.bairesdev.com/solutions/ios/',
            label: 'iOS App Development',
            children: [],
          },
          {
            id: 'cG9zdDo2MDfdgdkwMg==',
            path: 'https://www.bairesdev.com/solutions/ui-ux/',
            label: 'UX/UI Design',
            children: [],
          },
          {
            id: 'cG9zdDo2asdfaMDkwMw==',
            path: 'https://www.bairesdev.com/solutions/android/',
            label: 'Android App Development',
            children: [],
          },
          {
            id: 'cG9zdDo2MDsdfgskwMQ==',
            path: 'https://www.bairesdev.com/solutions/machine-learning/',
            label: 'Machine Learning',
            children: [],
          },
          {
            id: 'cG9zasdfdDo2MDkwMg==',
            path: 'https://www.bairesdev.com/solutions/database/',
            label: 'Data Engineering',
            children: [],
          },
          {
            id: 'cG9zdDhjho2MDkwMw==',
            path: 'https://www.bairesdev.com/software-development-services/front-end/',
            label: 'Front-end Development',
            children: [],
          },
        ],
      },
      {
        id: 'ddddergbvbfgcc',
        path: '',
        label: 'Enterprise',
        children: [
          {
            id: 'dG9zdDo2MasdfDkwMQ==',
            path: 'https://www.bairesdev.com/technologies/servicenow/',
            label: 'ServiceNow',
            children: [],
          },
          {
            id: 'dG9zdDo2MDfgdkwMg==',
            path: 'https://www.bairesdev.com/technologies/salesforce/',
            label: 'Salesforce',
            children: [],
          },
          {
            id: 'dG9zdDoadfgr2MDkwMw==',
            path: 'https://www.bairesdev.com/technologies/oracle/',
            label: 'Oracle',
            children: [],
          },
          {
            id: 'dG9zdDo2MrtrethytrhDkwMQ==',
            path: 'https://www.bairesdev.com/technologies/aws/',
            label: 'AWS',
            children: [],
          },
          {
            id: 'dG9zdDo2MDfdgdkwMg==',
            path: 'https://www.bairesdev.com/technologies/google-cloud/',
            label: 'Google Cloud',
            children: [],
          },
          {
            id: 'dG9zdDo2asdfaMDkwMw==',
            path: 'https://www.bairesdev.com/technologies/coldfusion/',
            label: 'Adobe ColdFusion',
            children: [],
          },
          {
            id: 'dG9zdDo2MDsdfgskwMQ==',
            path: 'https://www.bairesdev.com/technologies/elasticsearch/',
            label: 'Elasticsearch',
            children: [],
          },
          {
            id: 'dG9zasdfdDo2MDkwMg==',
            path: 'https://www.bairesdev.com/solutions/cybersecurity/',
            label: 'Cybersecurity',
            children: [],
          },
          {
            id: 'dG9zdDhjho2MDkwMw==',
            path: 'https://www.bairesdev.com/solutions/ecommerce/',
            label: 'eCommerce',
            children: [],
          },
        ],
      },
    ],
  },
  {
    id: 'cG9zdDo2MDkwNA==',
    path: '/clients/',
    label: 'Our Clients',
    children: [],
  },
  {
    id: 'cG9zdDo2MDkxMQ==',
    path: '/about/',
    label: 'About Us',
    children: [
      {
        id: 'cG9zdDogfe5ewNw==',
        path: '/about/',
        label: 'About Us',
        description: 'Who we are and how our vision became reality.',
        children: [],
      },
      {
        id: 'cG9zdDo2MDkxNw==',
        path: '/about/leadership-team/',
        label: 'Our Leadership Team',
        description: 'Industry-recognized. Award-winning.',
        children: [],
      },
      {
        id: 'cG9zdDo2MDkxOA==',
        path: 'https://www.bairesdev.com/top-1-percent/',
        label: 'Top 1% Talent',
        children: [],
      },
      {
        id: 'cG9zdDo2MDkyMA==',
        path: '/awards-recognitions/',
        label: 'Recognitions',
        children: [],
      },
      {
        id: 'cG9zdDo2MDkyMQ==',
        path: 'https://www.bairesdev.com/diversity-inclusion/',
        label: 'Diversity & Inclusion',
        children: [],
      },
      {
        id: 'cG9zdDo2MDkyMg==',
        path: 'https://www.bairesdev.com/software-development-services/expertise/',
        label: 'Expertise',
        children: [],
      },
      {
        id: 'cG9zdDo2MDkyMR==',
        path: 'https://www.bairesdev.com/contact-us/',
        label: 'Contact Us',
        children: [],
      },
    ],
  },
  {
    id: 'cG9zdDo2MDkxMg==',
    path: '/blog/',
    label: 'Blog',
    children: [],
  },
  {
    id: 'cG9zdDo2MDkxMw==',
    path: 'https://www.bairesdev.com/join-us/',
    label: 'Careers',
    children: [
      {
        id: 'cG9zd353kh3DkxNQ==',
        path: 'https://www.bairesdev.com/join-us/',
        label: 'Careers',
        description: 'What is it like working at BairesDev?',
        children: [],
      },
      {
        id: 'cG9zdDo2MDkxNQ==',
        path: 'https://talent.bairesdev.com/',
        label: 'Job Opportunities',
        children: [],
      },
      {
        id: 'cG9zdDo2MDkxNg==',
        path: 'https://www.bairesdev.com/referrals-program/',
        label: 'Talent Referrals',
        children: [],
      },
    ],
  },
];
export default function withLayoutHOC(Component: NextComponentType<NextPageContext, any, {}>) {
  return function ComponentWithFooter(props: any) {
    const {
      menuItems,
      seo,
      year,
      searchEngineDiscovery,
      isPaginated,
      removeBlogUrl,
      bgFooter = 'Light',
      ...componentProps
    } = props;

    // @TODO get menu data from wp
    // const mainMenu = getMenuTree(menuItems, MenuLocationEnum.Main);
    const blogMenu = getMenuTree(menuItems, MenuLocationEnum.Blog);

    const footerMenus: FooterMenu[] = [
      {
        title: menuItems[MenuLocationEnum.Footer_1].name,
        menuItems: getMenuTree(menuItems, MenuLocationEnum.Footer_1),
      },
      {
        title: menuItems[MenuLocationEnum.Footer_2].name,
        menuItems: getMenuTree(menuItems, MenuLocationEnum.Footer_2),
      },
      {
        title: menuItems[MenuLocationEnum.Footer_3].name,
        menuItems: getMenuTree(menuItems, MenuLocationEnum.Footer_3),
      },
    ];

    const pageTitle = extractText(seo?.openGraph?.title ?? componentProps?.pageName);

    useEffect(() => {
      mutiny.sendMutinyExperienceToSegment(process.env.NEXT_PUBLIC_GA4);
    }, [pageTitle]);

    const scriptProps = seoScriptProps(seo?.jsonLd, parseImageUrl);
    const { asPath } = useRouter();
    const path = asPath.split('#')[0].split('?')[0];
    const host = `${process.env.NEXT_PUBLIC_HOSTNAME}${removeBlogUrl ? '' : process.env.NEXT_PUBLIC_BASE_PATH}`;
    const canonicalLink = { href: `${host}${path}`, rel: 'canonical' };
    const paginationLinks = isPaginated
      ? getPaginationLinks(path, componentProps?.page ?? 1, componentProps?.total ?? 0, host)
      : [];

    useEffect(
      () => () => {
        const seoElements = seo ? metaTags(seo) : [];
        removeMetaTags(seoElements);
      },
      [seo],
    );

    return (
      <>
        <Head>
          {seo && metaTags(seo)}
          {scriptProps && <script {...scriptProps} />}
          {linkTags([canonicalLink, ...paginationLinks])}
          {(!searchEngineDiscovery || !seo || process.env.NEXT_PUBLIC_ENABLE_ROBOTS === 'false') && (
            <meta name="robots" key="robots" content="nofollow, noindex, noarchive, noimageindex, nosnippet" />
          )}
        </Head>
        <ThemeProvider theme={ThemeEnum.Site}>
          <BairesDevHeader
            animatedHeader={false}
            enableHeaderBehavior
            menuItems={mainMenuMockData}
            logoPath={`${process.env.NEXT_PUBLIC_BASE_PATH}/bairesdev-logo-light.svg`}
            logoSize={{ width: 142, height: 21 }}
            superMenu
            headerLinkClasses="flex items-center gap-8 h-11"
            headerLinkChildren={
              <>
                <div className="bg-site-neutrals-50 w-0.5 self-stretch rounded" />
                <a
                  href={`${process.env.NEXT_PUBLIC_HOSTNAME}${process.env.NEXT_PUBLIC_BASE_PATH}`}
                  className="site-display-xs text-site-neutrals-800 font-light"
                >
                  Blog
                </a>
              </>
            }
            CtaButton={
              <ButtonLink<ThemeEnum.Site>
                variant="Primary"
                variantColor="Black"
                size="Medium"
                classes="ml-8 mdL:ml-2"
                href="https://www.bairesdev.com/start/basic-details/"
              >
                Schedule a Call
              </ButtonLink>
            }
          />
          <Navigation
            list={{
              title: 'Category',
              links: blogMenu.map((item) => ({ label: item.label, href: item.path })),
            }}
            visibleInPaths={[
              '/',
              '/author/[slug]/[[...page]]',
              '/category/[slug]/[[...page]]',
              '/page/[page]',
              '/search',
              '/search/not-found/page/[page]',
              '/tag/[slug]/[[...page]]',
              '/authors/[[...page]]',
            ]}
          />
          <Component {...componentProps} />
          <BairesDevFooter<ThemeEnum.Site>
            year={year}
            contactusUrl={addUrlHostName('/contact-us/')}
            placeholder="name@email.com"
            label="Your e-mail address"
            hasSocialTitle
            bgFooter={bgFooter}
            logoPath={`${process.env.NEXT_PUBLIC_BASE_PATH ?? ''}/bairesdev-logo-footer.svg`}
            logoSize={[60, 40]}
            menus={footerMenus}
            subscriptionApi={`${process.env.NEXT_PUBLIC_BASE_PATH}/api/subscribe/`}
            buttonsProps={{
              scheduleCallBtn: {
                variant: 'Primary',
                icon: ButtonArrowIcon,
              },
              contactUsBtn: {
                variant: 'Secondary',
                variantColor: 'Black',
              },
              subscriptionBtn: {
                variant: 'Secondary',
                variantColor: 'Black',
                icon: EmailIcon,
                iconPosition: 'right',
              },
            }}
            withFullStops
          />
          <CookieBar />
        </ThemeProvider>
      </>
    );
  };
}
